import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StoreServices from '../../../services/Store';
import BayServices from '../../../services/Bay';
import SectionServices from '../../../services/Section';
import SaleInvoiceServices from '../../../services/SaleInvoice';
import ReportServices from '../../../services/Report';
import { generatePdf } from '../../../utils/helper';

/**
 * Dedicated Refund Form Component
 * Handles refund operations with specific fee calculations based on delivery status
 *
 * Refund Scenarios:
 * - Before Delivery: Full refund (item price + tax)
 * - After Delivery: 20% fee + pickup charges + tax
 */
const RefundForm = ({ preselectedInvoices, preselectedItems, onSubmit, onClose }) => {
  // State
  const navigate = useNavigate();
  const [selectedInvoices, setSelectedInvoices] = useState(preselectedInvoices || []);
  const [selectedItems, setSelectedItems] = useState(preselectedItems || []);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [itemActions, setItemActions] = useState({});

  // Data for dropdowns
  const [stores, setStores] = useState([]);
  const [bays, setBays] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Estimate state for realtime calculations
  const [estimate, setEstimate] = useState({
    refunds: [],
    totals: {
      refundNet: 0,
      totalFees: 0,
      pickupCharges: 0,
      taxAmount: 0,
    },
  });
  const [estimating, setEstimating] = useState(false);
  const invoiceRef = useRef(null);

  // Invoice search
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceResults, setInvoiceResults] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Fetch stores, bays, sections on mount
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      StoreServices.getStores({ active: true }),
      BayServices.getBays({ active: true }),
      SectionServices.getSections({ active: true })
    ]).then(([storeRes, bayRes, sectionRes]) => {
      setStores(storeRes.data || storeRes || []);
      setBays(bayRes.data || bayRes || []);
      setSections(sectionRes.data || sectionRes || []);
    }).catch(() => {
      setError('Failed to load store/bay/section data');
    }).finally(() => setLoading(false));
  }, []);

  // Fetch items for preselectedInvoices if needed
  useEffect(() => {
    const needsFetch =
      (preselectedInvoices &&
        (
          !preselectedItems ||
          preselectedItems.length === 0 ||
          preselectedItems.some(item => !item.name)
        )
      );
    if (needsFetch) {
      setLoading(true);
      Promise.all(
        preselectedInvoices.map(inv =>
          SaleInvoiceServices.getSaleInvoiceById(inv.id).then(res => {
            const invoiceData = (res.data && res.data[0]) || res;
            return invoiceData && invoiceData.sale_invoice_items
              ? invoiceData.sale_invoice_items
                .filter(item => (item.quantity || 0) > 0)
                .map(item => ({
                  ...item,
                  availableQuantity: item.quantity || 0,
                  invoiceId: inv.id
                }))
              : [];
          })
        )
      )
        .then(results => {
          setSelectedItems(results.flat());
        })
        .catch(() => setSelectedItems([]))
        .finally(() => setLoading(false));
    } else if (preselectedItems && preselectedItems.length > 0) {
      const filtered = preselectedItems
        .filter(item => (item.quantity - (item.refunded_quantity || 0)) > 0)
        .map(item => ({
          ...item,
          availableQuantity: item.quantity - (item.refunded_quantity || 0),
        }));
      setSelectedItems(filtered);
      // Initialize actions for preselected items
      const initialActions = {};
      filtered.forEach(item => {
        initialActions[item.id] = {
          quantity: item.availableQuantity || 1,
          storeId: '',
          bayId: '',
          sectionId: '',
          refundMethod: '',
          reason: ''
        };
      });
      setItemActions(initialActions);
      setSelectedItemIds(new Set(filtered.map(item => item.id)));
    }
  }, [preselectedInvoices, preselectedItems]);

  // Handlers
  const handleActionChange = (itemId, field, value) => {
    setItemActions(prev => {
      const existing = prev[itemId] || {};
      const updated = { ...prev, [itemId]: { ...existing, [field]: value } };
      return updated;
    });
  };

  const handleItemSelection = (itemId, isSelected) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(itemId);
        // Initialize actions for selected items
        setItemActions(prevActions => {
          const newActions = { ...prevActions };
          if (!newActions[itemId]) {
            newActions[itemId] = {
              quantity: selectedItems.find(item => item.id === itemId)?.availableQuantity || 1,
              storeId: '',
              bayId: '',
              sectionId: '',
              refundMethod: '',
              reason: ''
            };
          }
          return newActions;
        });
      } else {
        newSet.delete(itemId);
        // Clear actions for unselected items
        setItemActions(prevActions => {
          const newActions = { ...prevActions };
          delete newActions[itemId];
          return newActions;
        });
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allIds = selectedItems.map(item => item.id);
      setSelectedItemIds(new Set(allIds));
      // Initialize actions for all selected items
      setItemActions(prevActions => {
        const newActions = { ...prevActions };
        allIds.forEach(itemId => {
          if (!newActions[itemId]) {
            const item = selectedItems.find(i => i.id === itemId);
            newActions[itemId] = {
              quantity: item?.availableQuantity || 1,
              storeId: '',
              bayId: '',
              sectionId: '',
              refundMethod: '',
              reason: ''
            };
          }
        });
        return newActions;
      });
    } else {
      setSelectedItemIds(new Set());
      setItemActions({});
    }
  };

  // Filter helpers
  const getBaysForStore = (storeId) => bays.filter(b => b.store_id === Number(storeId));
  const getSectionsForStoreBay = (storeId, bayId) => sections.filter(s => s.store_id === Number(storeId) && s.bay_id === Number(bayId));

  // Invoice search
  const handleInvoiceSearch = async () => {
    setInvoiceLoading(true);
    setError(null);
    try {
      const res = await SaleInvoiceServices.getSaleInvoices({ search: invoiceSearch, limit: 10 });
      setInvoiceResults(res.data || res || []);
    } catch (e) {
      setError('Failed to search invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleSelectInvoice = async (invoice) => {
    if (selectedInvoices.find(inv => inv.id === invoice.id)) return;
    setSelectedInvoices(prev => [...prev, invoice]);
    try {
      const res = await SaleInvoiceServices.getSaleInvoiceById(invoice.id);
      const invoiceData = (res.data && res.data[0]) || res;
      if (invoiceData && invoiceData.sale_invoice_items) {
        setSelectedItems(prev => ([...prev, ...invoiceData.sale_invoice_items.map(item => ({ ...item, invoiceId: invoice.id }))]));
      }
    } catch {
      setError('Failed to fetch invoice items');
    }
  };

  const handleRemoveInvoice = (invoiceId) => {
    setSelectedInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    setSelectedItems(prev => prev.filter(item => item.invoiceId !== invoiceId));
    setItemActions(prev => {
      const newActions = { ...prev };
      Object.keys(newActions).forEach(itemId => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (item && item.invoiceId === invoiceId) delete newActions[itemId];
      });
      return newActions;
    });
  };

  // Invoice/item selection UI
  const renderInvoiceItemSelection = () => {
    if (preselectedInvoices && preselectedItems) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <div>
          <input
            type="text"
            placeholder="Search invoices..."
            value={invoiceSearch}
            onChange={e => setInvoiceSearch(e.target.value)}
            style={{ marginRight: 8 }}
          />
          <button onClick={handleInvoiceSearch} disabled={invoiceLoading || !invoiceSearch}>Search</button>
        </div>
        {invoiceLoading && <div>Searching...</div>}
        {invoiceResults.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Results:</b>
            <ul>
              {invoiceResults.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleSelectInvoice(inv)}>Select</button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {selectedInvoices.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <b>Selected Invoices:</b>
            <ul>
              {selectedInvoices.map(inv => (
                <li key={inv.id}>
                  #{inv.id} - {inv.order_number || inv.id} - {inv.billing_name} {inv.billing_last_name}
                  <button style={{ marginLeft: 8 }} onClick={() => handleRemoveInvoice(inv.id)}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Build table rows for refund
  const renderTableRows = () => {
    if (!selectedItems.length) return <tr><td colSpan={7}>No items selected.</td></tr>;
    return selectedItems.map(item => {
      const action = itemActions[item.id] || {};
      const baysForStore = action.storeId ? getBaysForStore(action.storeId) : [];
      const sectionsForStoreBay = (action.storeId && action.bayId) ? getSectionsForStoreBay(action.storeId, action.bayId) : [];

      const itemObj = item.item || {};
      const variantObj = item.variant || {};
      const name = itemObj.name || item.name || item.item_name || 'Unnamed Item';
      let attrStr = '';
      if (variantObj.attributes && typeof variantObj.attributes === 'object') {
        attrStr = Object.entries(variantObj.attributes)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' • ');
      } else if (item.variant_options && typeof item.variant_options === 'object') {
        attrStr = Object.entries(item.variant_options)
          .map(([k, vals]) => `${k}: ${Array.isArray(vals) ? vals.join('/') : vals}`)
          .join(' • ');
      }
      const displayName = attrStr ? `${name} — ${attrStr}` : name;

      return (
        <tr key={item.id}>
          <td style={{ padding: '6px 4px' }}>
            <input
              type="checkbox"
              checked={selectedItemIds.has(item.id)}
              onChange={(e) => handleItemSelection(item.id, e.target.checked)}
              style={{ margin: 0 }}
            />
          </td>
          <td style={{ padding: '6px 4px', fontWeight: 500, fontSize: 13 }}>{displayName}</td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.storeId || ''} onChange={e => handleActionChange(item.id, 'storeId', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Store</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.bayId || ''} onChange={e => handleActionChange(item.id, 'bayId', e.target.value)} disabled={!selectedItemIds.has(item.id) || !action.storeId} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Bay</option>
              {baysForStore.map(bay => (
                <option key={bay.id} value={bay.id}>{bay.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.sectionId || ''} onChange={e => handleActionChange(item.id, 'sectionId', e.target.value)} disabled={!selectedItemIds.has(item.id) || !action.storeId || !action.bayId} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Section</option>
              {sectionsForStoreBay.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <input type='number' min={1} max={item.availableQuantity} value={action.quantity || item.availableQuantity} onChange={e => handleActionChange(item.id, 'quantity', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }} />
          </td>
          <td style={{ padding: '6px 4px' }}>
            <select value={action.refundMethod || ''} onChange={e => handleActionChange(item.id, 'refundMethod', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }}>
              <option value=''>Select Method</option>
              <option value='cash'>Cash</option>
              <option value='credit'>Credit</option>
              <option value='store_credit'>Store Credit</option>
              <option value='original_payment'>Original Payment</option>
            </select>
          </td>
          <td style={{ padding: '6px 4px' }}>
            <input type='text' value={action.reason || ''} onChange={e => handleActionChange(item.id, 'reason', e.target.value)} disabled={!selectedItemIds.has(item.id)} style={{ width: '100%', padding: '4px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ccc' }} />
          </td>
        </tr>
      );
    });
  };

  // Submit handler for refunds
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const refundMap = {};
      Object.entries(itemActions).forEach(([itemId, action]) => {
        const item = selectedItems.find(i => i.id === Number(itemId));
        if (!item || !action.refundMethod) return;

        if (!refundMap[item.invoiceId]) refundMap[item.invoiceId] = { items: [], refundMethod: action.refundMethod, reason: action.reason };
        refundMap[item.invoiceId].items.push({
          itemId: item.item_id || item.id,
          saleInvoiceItemId: item.id,
          quantity: Number(action.quantity || item.availableQuantity || 1),
          adjustedPrice: undefined,
          reason: action.reason,
          storeId: Number(action.storeId) || null,
          bayId: Number(action.bayId) || null,
          sectionId: Number(action.sectionId) || null,
        });
        refundMap[item.invoiceId].refundMethod = action.refundMethod;
      });

      const refunds = Object.entries(refundMap).map(([invoiceId, data]) => ({
        transactionId: Number(invoiceId),
        items: data.items,
        refundMethod: data.refundMethod,
        reason: data.reason,
      }));

      const payload = { refunds, exchanges: [] };
      const res = await ReportServices.createRefundExchange(payload);
      setSuccess('Refund submitted successfully!');
      if (onSubmit) onSubmit(payload, res);
    } catch (e) {
      setError('Failed to submit refund');
    } finally {
      setSubmitting(false);
    }
  };

  // Print estimate / preview
  const handlePrintEstimate = async () => {
    if (!invoiceRef?.current) {
      console.error('Invoice ref is not available');
      return;
    }

    console.log('Starting PDF generation for refund estimate');
    console.log('Invoice ref content:', invoiceRef.current);
    console.log('Invoice ref innerHTML:', invoiceRef.current.innerHTML);

    try {
      await generatePdf([invoiceRef], 'refund-estimate');
      console.log('PDF generation completed successfully');
    } catch (err) {
      console.error('Failed to generate PDF estimate', err);
    }
  };

  // Server-side estimate fetcher for refunds
  const fetchServerEstimate = async () => {
    setEstimating(true);
    try {
      const ia = itemActions || {};
      const si = selectedItems || [];

      console.log('fetchServerEstimate - itemActions:', ia);
      console.log('fetchServerEstimate - selectedItems:', si);

      const refundMap = {};
      selectedItemIds.forEach(itemId => {
        const item = si.find(i => i.id === Number(itemId));
        const action = ia[itemId];
        if (!item || !action || !action.refundMethod) return;

        console.log('Processing item:', itemId, 'action:', action);

        if (!refundMap[item.invoiceId]) refundMap[item.invoiceId] = { items: [], refundMethod: action.refundMethod, reason: action.reason };
        refundMap[item.invoiceId].items.push({
          itemId: item.item_id || item.id,
          saleInvoiceItemId: item.id,
          quantity: Number(action.quantity || item.availableQuantity || 1),
          adjustedPrice: undefined,
          reason: action.reason,
          storeId: Number(action.storeId) || null,
          bayId: Number(action.bayId) || null,
          sectionId: Number(action.sectionId) || null,
        });
      });

      const refunds = Object.entries(refundMap).map(([invoiceId, data]) => ({
        transactionId: Number(invoiceId),
        items: data.items,
        refundMethod: data.refundMethod,
        reason: data.reason,
      }));

      const payload = { refunds, exchanges: [] };
      console.log('Sending payload to calculate:', payload);

      const res = await ReportServices.calculateRefundExchange(payload);
      const data = res?.data ?? res;
      console.log('Received calculation response:', data);

      if (data) setEstimate(data);
    } catch (err) {
      console.error('fetchServerEstimate error', err);
      setEstimate({ refunds: [], totals: { refundNet: 0, totalFees: 0, pickupCharges: 0, taxAmount: 0 } });
    } finally {
      setEstimating(false);
    }
  };

  // Memoize the calculation data to prevent unnecessary re-executions
  const calculationData = useMemo(() => {
    const data = [];
    selectedItemIds.forEach(itemId => {
      const item = selectedItems.find(i => i.id === Number(itemId));
      const action = itemActions[itemId];
      if (item && action) {
        data.push({
          itemId: item.id,
          quantity: action.quantity || item.availableQuantity || 1,
          storeId: action.storeId,
          refundMethod: action.refundMethod
        });
      }
    });
    return data;
  }, [selectedItemIds, itemActions, selectedItems]);

  // Debounced effect to call server estimate
  useEffect(() => {
    const t = setTimeout(() => {
      if (calculationData.length > 0) {
        fetchServerEstimate();
      } else {
        setEstimate({ refunds: [], totals: { refundNet: 0, totalFees: 0, pickupCharges: 0, taxAmount: 0 } });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [calculationData]);

  // Render component
  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div className="card-header" style={{
        background: 'linear-gradient(135deg, #dc3545 0%, #b02a37 100%)',
        color: 'white',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        padding: '20px'
      }}>
        <div className="d-flex align-items-center">
          <i className="fas fa-undo-alt me-3" style={{ fontSize: '24px' }}></i>
          <div>
            <h4 className="mb-1" style={{ fontSize: '24px', fontWeight: '700' }}>Refund Request</h4>
            <p className="mb-0" style={{ opacity: 0.9 }}>
              Process refund for invoice #{preselectedInvoices && preselectedInvoices[0]?.order_number}
            </p>
          </div>
        </div>
      </div>

      <div className="card-body" style={{ padding: '20px' }}>
        {/* Invoice Information Section - Compact Layout */}
        {preselectedInvoices && preselectedInvoices.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <div className="card" style={{ border: '1px solid #e9ecef', borderRadius: '6px' }}>
                <div className="card-header" style={{
                  background: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  borderRadius: '5px 5px 0 0',
                  padding: '10px 15px'
                }}>
                  <h6 className="mb-0" style={{ color: '#495057', fontWeight: '600', fontSize: '14px' }}>
                    <i className="fas fa-file-invoice me-2"></i>
                    Original Invoice Information
                  </h6>
                </div>
                <div className="card-body" style={{ padding: '15px' }}>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Order Number
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#007bff' }}>
                          {preselectedInvoices[0].order_number || preselectedInvoices[0].id}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Order Date
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          {preselectedInvoices[0].order_date ? new Date(preselectedInvoices[0].order_date).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Total Amount
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#28a745' }}>
                          ${parseFloat(preselectedInvoices[0].total || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Customer Name
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          {preselectedInvoices[0].billing_name} {preselectedInvoices[0].billing_last_name}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Delivery Status
                        </label>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>
                          <span className={`badge ${preselectedInvoices[0].status?.toLowerCase().includes('deliv') ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '11px', padding: '3px 6px' }}>
                            {preselectedInvoices[0].status || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                          Contact
                        </label>
                        <div style={{ fontSize: '13px' }}>
                          <i className="fas fa-phone me-1"></i>
                          {preselectedInvoices[0].billing_phone_number_one}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Items Section - Full Width */}
        <div className="row mb-4">
          <div className="col-lg-12">
            <div className="card" style={{ border: '1px solid #007bff', borderRadius: '6px' }}>
              <div className="card-header" style={{
                background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                color: 'white',
                borderBottom: 'none',
                borderRadius: '5px 5px 0 0',
                padding: '10px 15px'
              }}>
                <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                  <i className="fas fa-list me-2"></i>
                  Items to Refund
                </h6>
              </div>
              <div className="card-body" style={{ padding: '15px' }}>
                <div className="alert alert-info" style={{ borderRadius: '4px', border: 'none', padding: '8px 12px', fontSize: '12px', marginBottom: '15px' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Refund Policy:</strong> {
                    preselectedInvoices && preselectedInvoices[0]?.status?.toLowerCase().includes('deliv')
                      ? '20% processing fee will be applied for delivered items, plus pickup charges.'
                      : 'Full refund available for undelivered items with no processing fees.'
                  }
                </div>
                {renderInvoiceItemSelection()}
                <div style={{ overflowX: 'auto', minHeight: 200, marginBottom: 15 }}>
                  <table border="0" cellPadding="0" style={{ width: '100%', minWidth: 800, tableLayout: 'fixed', background: '#fff', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: '#f8f9fa' }}>
                      <tr>
                        <th style={{ width: 40, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>
                          <input
                            type="checkbox"
                            checked={selectedItemIds.size === selectedItems.length && selectedItems.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                        </th>
                        <th style={{ width: 180, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Item</th>
                        <th style={{ width: 120, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Store</th>
                        <th style={{ width: 100, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Bay</th>
                        <th style={{ width: 120, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Section</th>
                        <th style={{ width: 80, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Qty</th>
                        <th style={{ width: 120, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Method</th>
                        <th style={{ width: 150, padding: '8px 6px', fontWeight: 500, fontSize: 13, borderBottom: '1px solid #dee2e6' }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItems.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: '#aaa', fontSize: 14 }}>No items selected.</td></tr>
                      ) : renderTableRows()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Calculation Summary - Centered Below */}
        <div className="row">
          <div className="col-lg-6 offset-lg-6">
            <div className="card" style={{ border: '1px solid #007bff', borderRadius: '6px', position: 'sticky', top: '20px' }}>
              <div className="card-header" style={{
                background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                color: 'white',
                borderBottom: 'none',
                borderRadius: '5px 5px 0 0',
                padding: '10px 15px'
              }}>
                <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                  <i className="fas fa-calculator me-2"></i>
                  Refund Summary
                </h6>
              </div>
              <div className="card-body" style={{ padding: '15px' }}>
                {/* Itemized Breakdown */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px'
                  }}>
                    <span style={{ color: '#666' }}>Original Value</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(estimate.totals.subTotal || 0).toFixed(2)}
                    </span>
                  </div>

                  {estimate.totals.totalFees > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#e74c3c',
                      fontSize: '13px'
                    }}>
                      <span>Processing Fees (20%)</span>
                      <span style={{ fontWeight: '500' }}>
                        -${Number(estimate.totals.totalFees || 0).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {estimate.totals.pickupCharges > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 0',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#e74c3c',
                      fontSize: '13px'
                    }}>
                      <span>Pickup Charges</span>
                      <span style={{ fontWeight: '500' }}>
                        -${Number(estimate.totals.pickupCharges || 0).toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px'
                  }}>
                    <span style={{ color: '#666' }}>Subtotal</span>
                    <span style={{ fontWeight: '500' }}>
                      {/* ${Number((estimate.totals.refundNet || 0) - (estimate.totals.totalFees || 0) - (estimate.totals.pickupCharges || 0)).toFixed(2)} */}
                      ${Number((estimate.totals.subTotal || 0) - (estimate.totals.totalFees || 0) - (estimate.totals.pickupCharges || 0)).toFixed(2)}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px'
                  }}>
                    <span style={{ color: '#666' }}>Tax Amount</span>
                    <span style={{ fontWeight: '500' }}>
                      ${Number(estimate.totals.taxAmount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Final Total */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #007bff',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#007bff'
                    }}>
                      NET REFUND
                    </span>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '800',
                      color: '#28a745'
                    }}>
                      ${Number((estimate.totals.refundNet || 0) - (estimate.totals.totalFees || 0) - (estimate.totals.pickupCharges || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Fee Explanation */}
                <div style={{
                  padding: '8px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '3px',
                  fontSize: '11px',
                  color: '#856404',
                  lineHeight: '1.3'
                }}>
                  <i className="fas fa-info-circle me-1"></i>
                  <strong>Policy:</strong> {
                    preselectedInvoices && preselectedInvoices[0]?.status?.toLowerCase().includes('deliv')
                      ? '20% fee for delivered items. Pickup charges apply.'
                      : 'No fees for undelivered items.'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global loader overlay */}
        {(loading || submitting || estimating) && (
          <div id="global-loader" className="page-loader" style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9997,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)'
          }}>
            <div className="whirly-loader"></div>
          </div>
        )}


        {/* Action buttons */}
        <div className="row mt-3">
          <div className="col-lg-12">
            <div className="d-flex justify-content-between align-items-center">
              <button onClick={onClose} className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-arrow-left me-1"></i>
                Back to Invoice
              </button>
              <div className="d-flex gap-2">
                {/* <button onClick={handlePrintEstimate} className="btn btn-outline-primary btn-sm" disabled={estimating}>
                  <i className="fas fa-print me-1"></i>
                  {estimating ? 'Generating...' : 'Print Estimate'}
                </button> */}
                <button onClick={handleSubmit} className="btn btn-danger" disabled={submitting}>
                  <i className="fas fa-paper-plane me-1"></i>
                  {submitting ? 'Submitting...' : 'Submit Refund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden estimate for printing */}
      <div style={{ display: 'none' }}>
        <div ref={invoiceRef} style={{
          width: '950px',
          minHeight: '600px',
          padding: '24px',
          fontFamily: 'Arial, sans-serif',
          color: '#222',
          backgroundColor: '#ffffff',
          fontSize: '14px',
          lineHeight: '1.4',
          position: 'relative'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#333', fontSize: '24px' }}>REFUND ESTIMATE</h2>
          {selectedInvoices[0] ? (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '5px 0' }}><strong>Order:</strong> {selectedInvoices[0].order_number || selectedInvoices[0].id}</p>
              <p style={{ margin: '5px 0' }}><strong>Date:</strong> {selectedInvoices[0].order_date ? new Date(selectedInvoices[0].order_date).toLocaleDateString() : ''}</p>
              <p style={{ margin: '5px 0' }}><strong>Customer:</strong> {selectedInvoices[0].billing_name} {selectedInvoices[0].billing_last_name}</p>
              <p style={{ margin: '5px 0' }}><strong>Status:</strong> {selectedInvoices[0].status || 'N/A'}</p>
            </div>
          ) : null}
          <hr style={{ border: '1px solid #ccc', margin: '20px 0' }} />
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '18px' }}>Refund Summary</h4>
            <p style={{ margin: '8px 0' }}>Net Refund: ${Number(estimate.totals.refundNet || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Fees Deducted: ${Number(estimate.totals.totalFees || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Pickup Charges: ${Number(estimate.totals.pickupCharges || 0).toFixed(2)}</p>
            <p style={{ margin: '8px 0' }}>Tax Amount: ${Number(estimate.totals.taxAmount || 0).toFixed(2)}</p>
          </div>
          <hr style={{ border: '1px solid #ccc', margin: '20px 0' }} />
          <p style={{ fontSize: '12px', color: '#666', margin: '10px 0' }}>This is an estimate. Final amounts may vary when processed.</p>
        </div>
      </div>

      {success && <div style={{ color: 'green', marginTop: 14, fontSize: 17 }}>{success}</div>}
    </div>
  );
};

export default RefundForm;